import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter, useParams, useHistory } from "react-router-dom";

import {
  ROUTES,
  messages,
  EMPTY_STRING,
  RECAP_EVENT_FIELD,
  RECAP_FEEDBACK_ACTION,
  DEFAULT_TIMESTAMP,
} from "../../../../../../../constants";
import { NavigateToPath } from "../../../../../../../system";
import {
  getS3GalleryUrl,
  validateRecapFeedbackForm,
} from "../../../../../../../shared";
import { customerDashboardActions } from "../../../../../../../redux/actions";
import VideoModal from "../../../../../../../shared/components/Modal/VideoModal";
import SelectField from "../../../../../../../shared/components/Fields/SelectField";
import SectionHeader from "../../../../../../../shared/components/Events/SectionHeader";
import SquareImageSelection from "../../../../../../../shared/components/Fields/SquareImageSelection";
import SingleRecapFeedback from "../../../../../../../shared/components/RecapFeedback/SingleRecapFeedback";

const createInitialRecapFeedback = (eventGalleryId) => ({
  eventGalleryId,
  actionType: RECAP_FEEDBACK_ACTION.SIMPLE_FEEDBACK,
  timeRequested: DEFAULT_TIMESTAMP,
  filename: EMPTY_STRING,
  feedback: EMPTY_STRING,
  errors: {},
});

const ReecapFeedback = (props) => {
  const history = useHistory();
  const { groupEventId, eventId } = useParams();

  const { recapFeedback } = props;

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRecap, setSelectedRecap] = useState(null);
  const [recapFeedbackForm, setRecapFeedbackForm] = useState([]);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (groupEventId) {
      props.getCustomerRecapFeedback(groupEventId);
    }
    // eslint-disable-next-line
  }, [groupEventId]);

  useEffect(() => {
    if (recapFeedback) {
      setEvents(recapFeedback?.subEvents || []);
      setSelectedEvent(recapFeedback?.subEvents?.[0] || null);
    } else {
      setEvents([]);
    }
    // eslint-disable-next-line
  }, [recapFeedback]);

  useEffect(() => {
    if (selectedEvent) {
      setSelectedRecap(null);
      setRecapFeedbackForm([]);
    }
    // eslint-disable-next-line
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedRecap) {
      if (selectedRecap?.galleryChanges?.length > 0) {
        setRecapFeedbackForm(selectedRecap.galleryChanges);
      } else {
        setRecapFeedbackForm([createInitialRecapFeedback(selectedRecap?.id)]);
      }
    }
    // eslint-disable-next-line
  }, [selectedRecap]);

  const handleRecapFeedbackSuccess = () => {
    props.getCustomerRecapFeedback(groupEventId);
    setRecapFeedbackForm([]);
    setSelectedEvent(null);
    setSelectedRecap(null);
  };

  const handleFieldChange = (index, field, value) => {
    const updatedForm = [...recapFeedbackForm];
    const currentForm = updatedForm[index];

    currentForm[field] = value;

    // Clear error when user updates the field
    if (currentForm.errors && currentForm.errors[field]) {
      delete currentForm.errors[field];
    }

    setRecapFeedbackForm(updatedForm);
  };

  const handleSubmit = () => {
    const { updatedForm, isValid } =
      validateRecapFeedbackForm(recapFeedbackForm);

    setRecapFeedbackForm(updatedForm);

    if (isValid) {
      const dataToSend = updatedForm.map(({ errors, ...rest }) => rest);
      props.addCustomerRecapFeedback(dataToSend, handleRecapFeedbackSuccess);
    }
  };

  return (
    <div className="recap-feedback">
      {events.length > 0 ? (
        <>
          <SectionHeader
            eventId={eventId}
            groupId={groupEventId}
            itemCount={events.length || 0}
            isGroup={recapFeedback?.isGroup}
            title={
              recapFeedback?.isGroup
                ? recapFeedback?.eventGroupName
                : (events[0]?.eventName ?? EMPTY_STRING)
            }
          />
          {events.length > 1 && (
            <div className="my-3 col-md-6 col-12 m-0 p-0">
              <SelectField
                options={events}
                value={selectedEvent}
                valueKey="eventName"
                setValue={(event) => {
                  setSelectedEvent(event);
                }}
                labelKey="eventName"
                label="select an event:"
                placeholder="select an event"
              />
            </div>
          )}

          {selectedEvent?.recaps && selectedEvent?.recaps.length > 0 ? (
            <>
              <h5 className="py-2">select a recap to provide feedback on:</h5>
              <div className="d-flex images-container flex-wrap">
                {selectedEvent?.recaps.map((recap) => {
                  return (
                    <SquareImageSelection
                      key={recap.id}
                      src={recap.thumbnailUrl}
                      checkBoxId={recap.id}
                      id={recap}
                      setSelected={(recap) => {
                        setSelectedRecap(recap);
                      }}
                      selected={selectedRecap?.id === recap.id}
                      showTooltip={recap.status || !recap?.isAllow}
                      tooltipTitle={
                        recap?.status
                          ? messages.FEEDBACK_IS_ALREADY_SUBMITTED
                          : !recap?.isAllow
                            ? messages.FEEDBACK_TIMELIMIT_EXPIRED
                            : EMPTY_STRING
                      }
                      type={recap.type}
                      url={recap.url}
                      setMediaUrl={setMediaUrl}
                      setIsModalOpen={setIsModalOpen}
                    />
                  );
                })}
              </div>
              <p className="my-4 section-header">
                Please provide your feedback below for our editing team. Kindly
                note that while minor updates are included, more extensive
                revisions may incur a re-editing fee.
              </p>
              {recapFeedbackForm.map((form, index) => (
                <SingleRecapFeedback
                  key={form.id}
                  data={form}
                  onChange={(field, value) =>
                    handleFieldChange(index, field, value)
                  }
                  disabled={selectedRecap?.status || !selectedRecap?.isAllow}
                />
              ))}
              <div className="d-flex buttons my-5">
                <button
                  className="cancel"
                  onClick={() => NavigateToPath(history, ROUTES.MY_EVENTS_TAB)}
                >
                  cancel
                </button>
                <button
                  type="button"
                  className="submit"
                  disabled={
                    !recapFeedbackForm.length ||
                    selectedRecap?.status ||
                    !selectedRecap?.isAllow
                  }
                  onClick={handleSubmit}
                >
                  submit
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-content text-center mt-4">
                {messages.NO_RECAP_FOUND}
              </div>
            </>
          )}
        </>
      ) : (
        <p className="d-flex justify-content-center">no events found</p>
      )}

      <VideoModal
        videoUrl={getS3GalleryUrl(mediaUrl)}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setMediaUrl(null);
        }}
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userId: state.authentication.userId,
  recapFeedback: state.customerDashboard.recapFeedback,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getCustomerRecapFeedback: (eventGroupId) =>
    dispatch(customerDashboardActions.getCustomerRecapFeedback(eventGroupId)),
  addCustomerRecapFeedback: (data, onResponseCallback) =>
    dispatch(
      customerDashboardActions.addCustomerRecapFeedback(
        data,
        onResponseCallback,
      ),
    ),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ReecapFeedback));
